import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { ApiService } from '../service/core/api.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TeamService } from '../service/core/team.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SubscriptionService } from '../service/core/subscription.service';
import { TooltipModule } from 'primeng/tooltip';
@Component({
  standalone: true,
  selector: 'app-team',
  providers: [MessageService, ConfirmationService],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TableModule,
    ToastModule,
    ConfirmDialogModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    MultiSelectModule,
    TooltipModule
  ],
  templateUrl: './team.html',
  styleUrl: './team.scss'
})
export class Team implements OnInit {
agents: any[] = [];
  showAddDialog = false;
  loading = false;
  form: FormGroup;

  roleOptions = [
    { label: 'Agent', value: 'ROLE_AGENT' },
    { label: 'Manager', value: 'ROLE_MANAGER' },
    { label: 'Company Admin', value: 'ROLE_COMPANY_ADMIN' }
  ];

  constructor(
    private fb: FormBuilder,
    private teamService: TeamService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private subscriptionService: SubscriptionService
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      roles: [[], Validators.required],
      name:['', [Validators.required]],
      // Temporary password the admin sets and shares with the new user.
      // The user is forced to change it at first sign-in.
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit() { this.loadEmployees(); }

  loadEmployees() {
    this.teamService.getEmployees().subscribe(res => {
      this.agents = Array.isArray(res)
        ? res
        : (res as any)?.data ?? (res as any)?.employees ?? [];
    });
  }

  isInvalid(name: string) {
    const control = this.form.get(name);
    return control?.invalid && (control.dirty || control.touched);
  }

  openAddDialog() {
    if (!this.subscriptionService.canAddUser()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Limit Reached',
        detail: 'User limit reached for your current plan.'
      });
      return;
    }
    this.form.reset({ email: '', roles: ['ROLE_AGENT'], name: '', password: '' });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.showAddDialog = true;
  }

  get userLimitReached(): boolean {
    return !this.subscriptionService.canAddUser();
  }

  addEmployee() {
    if (!this.subscriptionService.canAddUser()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Limit Reached',
        detail: 'User limit reached for your current plan.'
      });
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.teamService.addEmployee(this.form.value).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Member added successfully' });
        this.showAddDialog = false;
        this.loading = false;
        this.loadEmployees();
        this.subscriptionService.fetchSubscription().subscribe({ error: () => {} });
      },
      error: (err: any) => {
        const detail = err?.errorCode
          ? `${err.message}`
          : (err?.message || 'Failed to add member');
        this.messageService.add({ severity: 'error', summary: 'Error', detail });
        this.loading = false;
      }
    });
  }

  /** Fill the temporary-password field with a strong, easy-to-read password. */
  generatePassword(): void {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'; // no I/O — easier to read aloud
    const lower = 'abcdefghijkmnopqrstuvwxyz'; // no l
    const digits = '23456789';                 // no 0/1
    const symbols = '!@#$%&*?';
    const all = upper + lower + digits + symbols;
    const rand = (set: string) => set[Math.floor(Math.random() * set.length)];
    // Guarantee one of each class, then pad to 12 and shuffle.
    let pwd = rand(upper) + rand(lower) + rand(digits) + rand(symbols);
    while (pwd.length < 12) pwd += rand(all);
    pwd = pwd.split('').sort(() => Math.random() - 0.5).join('');
    this.form.patchValue({ password: pwd });
    this.form.get('password')?.markAsDirty();
  }

  confirmDelete(agent: any): void {
    this.confirmationService.confirm({
      header: 'Remove team member',
      message: `Remove ${agent?.name || agent?.email}? They will lose access immediately. Their records stay intact and you can re-add them later.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.removeEmployee(agent)
    });
  }

  private removeEmployee(agent: any): void {
    this.teamService.deleteEmployee(agent.id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Removed', detail: 'Team member removed' });
        this.loadEmployees();
        this.subscriptionService.fetchSubscription().subscribe({ error: () => {} });
      },
      error: (err: any) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.message || 'Failed to remove member' });
      }
    });
  }
}
